import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Target, Loader2 } from "lucide-react";
import { useAuth, apiErr } from "@/context/AuthContext";
import Disclaimer from "@/components/Disclaimer";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      const pending = localStorage.getItem("tc_pending_invite");
      if (pending) {
        navigate(`/family?invite=${encodeURIComponent(pending)}`);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-tc-bg tc-grid-bg flex items-center justify-center p-6">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-10 items-center">
        {/* Left — brand */}
        <div className="hidden lg:flex flex-col animate-fade-up">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-tc-coach to-tc-mastery flex items-center justify-center shadow-lg shadow-tc-coach/30">
              <Target className="w-7 h-7 text-white" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-2xl tracking-tight">TRADECRAFT</div>
              <div className="text-xs font-mono uppercase tracking-widest text-tc-muted">Learn the market. Master the craft.</div>
            </div>
          </div>
          <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-tight">
            Your job is not to <span className="text-tc-bear">predict</span> the future.
          </h1>
          <p className="text-tc-sub mt-4 text-base leading-relaxed max-w-md">
            It is to make disciplined decisions under uncertainty. TRADECRAFT teaches the craft of
            trading through replay, simulation, and process — not profit.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {["PROCESS > PROFIT", "$10,000 sim account", "1% risk", "No real money"].map((t) => (
              <span key={t} className="text-xs font-mono px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-tc-sub">{t}</span>
            ))}
          </div>
          <div className="mt-10 max-w-md"><Disclaimer /></div>
        </div>

        {/* Right — form */}
        <div className="tc-card p-7 sm:p-9 animate-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-tc-coach to-tc-mastery flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-extrabold text-xl">TRADECRAFT</span>
          </div>
          <h2 className="font-heading text-2xl font-bold">Welcome back</h2>
          <p className="text-tc-sub text-sm mt-1">Sign in to continue your training.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-tc-muted">Email</label>
              <input data-testid="login-email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-lg bg-tc-bg border border-white/10 px-4 py-2.5 text-sm focus:border-tc-coach focus:ring-1 focus:ring-tc-coach outline-none transition-colors" />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-tc-muted">Password</label>
              <input data-testid="login-password" type="password" autoComplete="current-password" placeholder="Your password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full rounded-lg bg-tc-bg border border-white/10 px-4 py-2.5 text-sm focus:border-tc-coach focus:ring-1 focus:ring-tc-coach outline-none transition-colors" />
            </div>
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 hover:bg-tc-coach/90 transition-colors disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
            </button>
          </form>
          <div className="mt-5 flex items-center justify-between text-sm">
            <a href="/signup" data-testid="login-signup-link" className="text-tc-coach">Create account</a>
            <a href="/forgot-password" data-testid="login-forgot-link" className="text-tc-sub hover:text-tc-txt">Forgot password?</a>
          </div>
        </div>
      </div>
    </div>
  );
}
